import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE, page, cardScroll, inp, lbl, btn, btnDisabled, ConfirmDialog } from './AdminUsers';
import TopBanner from '../components/TopBanner';
import AdminSidebar from '../components/AdminSidebar';

// Application Management — admin-manageable replacement for what used to
// be a hardcoded APPS array in Dashboard.jsx. Same "whole-array-replace"
// convention as AdminHierarchy.jsx: every action (enable/disable, add/
// remove a group, add a new app, delete one) edits a local copy of the
// full apps array and PUTs the entire thing back to /api/apps/admin.

const EMPTY_FORM = { id: '', name: '', description: '', icon: '🔗', url: '', color: '#0F6E56', sso: false, enabled: true };

export default function AdminApps() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [apps, setApps] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [groupToAdd, setGroupToAdd] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [dialog, setDialog] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);

  // Guard: only bescom-super-admins. Two cases, not one — see
  // AdminApprovals.jsx's own comment on this same pattern.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/');
      return;
    }
    const groups = user.profile?.groups || [];
    if (!groups.includes('bescom-super-admins')) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    fetchApps();
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/admin/groups`, {
          credentials: 'include',
        });
        if (!res.ok) return;
        const data = await res.json();
        setAllGroups(data.results || []);
      } catch (e) {
        // Non-fatal — the "add group" pickers just won't have options.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchApps() {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/apps`);
      const data = await res.json();
      setApps(data.apps || []);
    } catch (e) {
      setError('Could not load applications.');
    } finally {
      setLoading(false);
    }
  }

  async function saveApps(nextApps) {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/apps/admin`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apps: nextApps }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not save changes.');
        return false;
      }
      setApps(nextApps);
      return true;
    } catch (e) {
      setError('Could not save changes.');
      return false;
    } finally {
      setSaving(false);
    }
  }

  function toggleEnabled(app) {
    const willEnable = app.enabled === false;
    setDialog({
      title: willEnable ? 'Enable application' : 'Disable application',
      message: willEnable
        ? `Enable "${app.name}"? It will reappear on the Dashboard for every group it's assigned to.`
        : `Disable "${app.name}"? It will disappear from everyone's Dashboard until re-enabled.`,
      confirmLabel: willEnable ? 'Enable' : 'Disable',
      danger: !willEnable,
      onConfirm: async () => {
        setDialog(null);
        const next = apps.map((a) => (a.id === app.id ? { ...a, enabled: willEnable } : a));
        await saveApps(next);
      },
    });
  }

  function handleAddGroup(app) {
    const groupName = groupToAdd[app.id];
    if (!groupName) return;
    setDialog({
      title: 'Add group',
      message: `Give "${groupName}" access to "${app.name}"?`,
      confirmLabel: 'Add',
      onConfirm: async () => {
        setDialog(null);
        const next = apps.map((a) =>
          a.id === app.id && !(a.groups || []).includes(groupName)
            ? { ...a, groups: [...(a.groups || []), groupName] }
            : a
        );
        setGroupToAdd((prev) => ({ ...prev, [app.id]: '' }));
        await saveApps(next);
      },
    });
  }

  function handleRemoveGroup(app, groupName) {
    setDialog({
      title: 'Remove group',
      message: `Remove "${groupName}"'s access to "${app.name}"? Members of that group will no longer see it on their Dashboard.`,
      confirmLabel: 'Remove',
      danger: true,
      onConfirm: async () => {
        setDialog(null);
        const next = apps.map((a) =>
          a.id === app.id ? { ...a, groups: (a.groups || []).filter((g) => g !== groupName) } : a
        );
        await saveApps(next);
      },
    });
  }

  function handleDeleteApp(app) {
    setDialog({
      title: 'Delete application',
      message: `Remove "${app.name}" from the portal? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        setDialog(null);
        await saveApps(apps.filter((a) => a.id !== app.id));
      },
    });
  }

  function handleAddFormChange(e) {
    const { name, value, type, checked } = e.target;
    setAddForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }

  async function handleAddApp(e) {
    e.preventDefault();
    const id = addForm.id.trim();
    if (!id || !addForm.name.trim() || !addForm.url.trim()) {
      setError('Id, name and URL are required.');
      return;
    }
    if (apps.some((a) => a.id === id)) {
      setError('An application with that id already exists.');
      return;
    }
    const newApp = { ...addForm, id, name: addForm.name.trim(), url: addForm.url.trim(), groups: [] };
    const ok = await saveApps([...apps, newApp]);
    if (ok) {
      setAddForm(EMPTY_FORM);
      setShowAddForm(false);
    }
  }

  function handleStartEdit(app) {
    setShowAddForm(false);
    setError(null);
    setEditingId(app.id);
    setEditForm({
      id: app.id,
      name: app.name || '',
      description: app.description || '',
      icon: app.icon || '🔗',
      url: app.url || '',
      color: app.color || '#0F6E56',
      sso: !!app.sso,
      enabled: app.enabled !== false,
    });
  }

  function handleCancelEdit() {
    setEditingId(null);
  }

  function handleEditFormChange(e) {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }

  // Id is fixed once created — it's what Dashboard.jsx's ICON_OVERRIDES
  // map and this app's group assignments key off of, so it isn't
  // editable here, only name/description/url/icon/color/sso. enabled and
  // groups are deliberately left untouched (edited via their own
  // confirmed actions elsewhere on this card), not pulled from editForm.
  async function handleSaveEdit(e, id) {
    e.preventDefault();
    if (!editForm.name.trim() || !editForm.url.trim()) {
      setError('Name and URL are required.');
      return;
    }
    const next = apps.map((a) =>
      a.id === id
        ? { ...a, name: editForm.name.trim(), description: editForm.description, icon: editForm.icon, url: editForm.url.trim(), color: editForm.color, sso: editForm.sso }
        : a
    );
    const ok = await saveApps(next);
    if (ok) {
      setEditingId(null);
    }
  }

  if (authLoading || !user) return null;

  return (
    <>
      <TopBanner />
      <div className="admin-page-wrap" style={page}>
        <div className="admin-row" style={{ display: 'flex', gap: '24px', width: '100%', height: '100%' }}>
        <AdminSidebar active="apps" />
        <div style={cardScroll}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1a1a1a' }}>Application Management</h2>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#5F5E5A' }}>
                Enable/disable applications shown on the Dashboard, control which groups can see each one, and add new applications.
              </p>
            </div>
            <button
              type="button"
              onClick={() => { setShowAddForm((v) => !v); setEditingId(null); setError(null); }}
              style={btn}
            >
              {showAddForm ? 'Cancel' : '+ Add Application'}
            </button>
          </div>

          {error && (
            <div style={{ marginBottom: '16px', padding: '12px 16px', background: '#FDECEC', color: '#B3261E', borderRadius: '8px', fontSize: '13px' }}>
              {error}
            </div>
          )}

          {showAddForm && (
            <form onSubmit={handleAddApp} style={{ marginBottom: '28px', padding: '20px', border: '1px solid #eee', borderRadius: '10px', background: '#fff' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                <div>
                  <label style={lbl}>Id (unique, no spaces)</label>
                  <input style={inp} name="id" value={addForm.id} onChange={handleAddFormChange} placeholder="e.g. billing" required />
                </div>
                <div>
                  <label style={lbl}>Name</label>
                  <input style={inp} name="name" value={addForm.name} onChange={handleAddFormChange} placeholder="e.g. BESCOM Billing App" required />
                </div>
                <div>
                  <label style={lbl}>Icon (emoji)</label>
                  <input style={inp} name="icon" value={addForm.icon} onChange={handleAddFormChange} maxLength={4} />
                </div>
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={lbl}>Description</label>
                <input style={inp} name="description" value={addForm.description} onChange={handleAddFormChange} placeholder="Short description shown on the card" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '14px', marginBottom: '14px' }}>
                <div>
                  <label style={lbl}>URL</label>
                  <input style={inp} name="url" value={addForm.url} onChange={handleAddFormChange} placeholder="https://..." required />
                </div>
                <div>
                  <label style={lbl}>Card accent color</label>
                  <input style={{ ...inp, padding: '4px', height: '40px' }} type="color" name="color" value={addForm.color} onChange={handleAddFormChange} />
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#1a1a1a', marginBottom: '4px' }}>
                <input type="checkbox" name="sso" checked={addForm.sso} onChange={handleAddFormChange} />
                This app has a one-click SSO shortcut route
              </label>
              <p style={{ margin: '0 0 18px', fontSize: '12px', color: '#888' }}>
                Every app here still authenticates through Authentik — this checkbox is not "does it use SSO?" (they all do). It's whether the app's own frontend has specifically been built with a <code>/login?sso=auto</code> route (like the Transfer and LC apps) that skips its manual "Login with SSO" click. Checking this for an app that <strong>doesn't</strong> have that route will send users to a broken URL and stop them logging in — leave it unchecked (Grafana, BICC Dashboard) unless you know that route exists.
              </p>
              <button type="submit" disabled={saving} style={saving ? btnDisabled : btn}>
                {saving ? 'Adding…' : 'Add Application'}
              </button>
            </form>
          )}

          {loading ? (
            <p style={{ color: '#5F5E5A' }}>Loading applications…</p>
          ) : apps.length === 0 ? (
            <p style={{ color: '#5F5E5A' }}>No applications configured yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {apps.map((app) => (
                <div key={app.id} style={{ border: '1px solid #eee', borderRadius: '10px', padding: '18px 20px', background: '#fff', borderLeft: `4px solid ${app.color || '#0F6E56'}` }}>
                  {editingId === app.id ? (
                    <form onSubmit={(e) => handleSaveEdit(e, app.id)}>
                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '14px', marginBottom: '14px' }}>
                        <div>
                          <label style={lbl}>Name</label>
                          <input style={inp} name="name" value={editForm.name} onChange={handleEditFormChange} required />
                        </div>
                        <div>
                          <label style={lbl}>Icon (emoji)</label>
                          <input style={inp} name="icon" value={editForm.icon} onChange={handleEditFormChange} maxLength={4} />
                        </div>
                      </div>
                      <div style={{ marginBottom: '14px' }}>
                        <label style={lbl}>Description</label>
                        <input style={inp} name="description" value={editForm.description} onChange={handleEditFormChange} />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '14px', marginBottom: '14px' }}>
                        <div>
                          <label style={lbl}>URL</label>
                          <input style={inp} name="url" value={editForm.url} onChange={handleEditFormChange} required />
                        </div>
                        <div>
                          <label style={lbl}>Card accent color</label>
                          <input style={{ ...inp, padding: '4px', height: '40px' }} type="color" name="color" value={editForm.color} onChange={handleEditFormChange} />
                        </div>
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#1a1a1a', marginBottom: '18px' }}>
                        <input type="checkbox" name="sso" checked={editForm.sso} onChange={handleEditFormChange} />
                        This app has a one-click SSO shortcut route (only if its own <code>/login?sso=auto</code> route exists)
                      </label>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button type="submit" disabled={saving} style={saving ? btnDisabled : { ...btn, width: 'auto' }}>
                          {saving ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          disabled={saving}
                          style={{ padding: '10px 20px', background: '#EDEBE4', border: 'none', color: '#5F5E5A', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
                        <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                          <span style={{ fontSize: '28px' }}>{app.icon}</span>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1a1a1a' }}>{app.name}</h3>
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: '5px',
                                fontSize: '11px', fontWeight: '700', letterSpacing: '0.02em',
                                padding: '2px 9px', borderRadius: '999px',
                                color: app.enabled === false ? '#B3261E' : '#0F6E56',
                                background: app.enabled === false ? '#FDECEC' : '#E6F4EF',
                              }}>
                                <span style={{
                                  width: '6px', height: '6px', borderRadius: '50%',
                                  background: app.enabled === false ? '#B3261E' : '#0F6E56',
                                }} />
                                {app.enabled === false ? 'DISABLED' : 'ENABLED'}
                              </span>
                            </div>
                            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#5F5E5A' }}>{app.description}</p>
                            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#888' }}>{app.url}</p>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                          <button
                            type="button"
                            onClick={() => handleStartEdit(app)}
                            disabled={saving}
                            style={{ background: '#009FE3', border: 'none', color: '#fff', borderRadius: '6px', fontSize: '12px', fontWeight: '600', padding: '6px 14px', cursor: 'pointer' }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleEnabled(app)}
                            disabled={saving}
                            style={{
                              background: app.enabled === false ? '#0F6E56' : '#B3261E',
                              border: 'none', color: '#fff', borderRadius: '6px',
                              fontSize: '12px', fontWeight: '600', padding: '6px 14px', cursor: 'pointer',
                            }}
                          >
                            {app.enabled === false ? 'Enable' : 'Disable'}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteApp(app)}
                            disabled={saving}
                            style={{ background: '#B3261E', border: 'none', color: '#fff', borderRadius: '6px', fontSize: '12px', fontWeight: '600', padding: '6px 14px', cursor: 'pointer' }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #f0f0f0' }}>
                        <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: '600', color: '#5F5E5A' }}>Groups that can see this application</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                          {(app.groups || []).length === 0 && (
                            <span style={{ fontSize: '13px', color: '#666' }}>No groups — nobody can see this app.</span>
                          )}
                          {(app.groups || []).map((g) => (
                            <span
                              key={g}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                background: '#EFF7FC', color: '#0072B1', border: '1px solid #D3E3ED',
                                borderRadius: '999px', padding: '4px 10px', fontSize: '12px',
                              }}
                            >
                              {g}
                              <button
                                type="button"
                                onClick={() => handleRemoveGroup(app, g)}
                                disabled={saving}
                                style={{ background: 'none', border: 'none', color: '#0072B1', cursor: 'pointer', fontSize: '13px', lineHeight: 1, padding: 0 }}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <select
                            value={groupToAdd[app.id] || ''}
                            onChange={(e) => setGroupToAdd((prev) => ({ ...prev, [app.id]: e.target.value }))}
                            style={{ ...inp, width: 'auto', minWidth: '220px' }}
                          >
                            <option value="">Add group…</option>
                            {allGroups
                              .filter((g) => !(app.groups || []).includes(g.name))
                              .map((g) => (
                                <option key={g.pk} value={g.name}>{g.name}</option>
                              ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => handleAddGroup(app)}
                            disabled={!groupToAdd[app.id] || saving}
                            style={(!groupToAdd[app.id] || saving) ? btnDisabled : { ...btn, width: 'auto' }}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        </div>
      </div>
      <ConfirmDialog dialog={dialog} onCancel={() => setDialog(null)} />
    </>
  );
}
